import { useState, useEffect, useCallback } from 'react'
import { PageMeta } from '@/components/seo/PageMeta'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PermissionGate } from '@/components/auth/permission-gate'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  WorkflowCard,
  WorkflowBuilder,
  CreateWorkflowDialog,
} from '@/components/approval'
import { approvalWorkflowService } from '@/lib/api/services'
import type {
  ApprovalWorkflow,
  ApprovalWorkflowDetail,
  ApprovalStage,
  CreateApprovalWorkflowRequest,
  CreateApprovalStageRequest,
  AddStageApproverRequest,
} from '@/lib/api/types'
import {
  Plus,
  RefreshCw,
  GitBranch,
  Loader2,
  AlertCircle,
  ShieldAlert,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export default function ApprovalWorkflowsPage() {
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteWorkflow, setDeleteWorkflow] = useState<ApprovalWorkflow | null>(null)
  const [editingWorkflow, setEditingWorkflow] = useState<ApprovalWorkflowDetail | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [saving, setSaving] = useState(false)

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await approvalWorkflowService.getWorkflows()
      setWorkflows(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadWorkflows()
  }, [loadWorkflows])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadWorkflows()
    } finally {
      setRefreshing(false)
    }
  }

  const handleCreate = async (data: CreateApprovalWorkflowRequest) => {
    const workflow = await approvalWorkflowService.createWorkflow(data)
    await loadWorkflows()
    // Open the editor for the new workflow
    const detail = await approvalWorkflowService.getWorkflow(workflow.id)
    setEditingWorkflow(detail)
  }

  const handleEdit = async (workflow: ApprovalWorkflow) => {
    try {
      const detail = await approvalWorkflowService.getWorkflow(workflow.id)
      setEditingWorkflow(detail)
    } catch (err) {
      console.error('Failed to load workflow:', err)
    }
  }

  const handleDelete = async () => {
    if (!deleteWorkflow) return

    setDeleting(true)
    try {
      await approvalWorkflowService.deleteWorkflow(deleteWorkflow.id)
      await loadWorkflows()
      setDeleteWorkflow(null)
    } catch (err) {
      console.error('Failed to delete workflow:', err)
    } finally {
      setDeleting(false)
    }
  }

  const handleSetDefault = async (workflow: ApprovalWorkflow) => {
    try {
      await approvalWorkflowService.updateWorkflow(workflow.id, { isDefault: true })
      await loadWorkflows()
    } catch (err) {
      console.error('Failed to set default:', err)
    }
  }

  const handleToggleStatus = async (workflow: ApprovalWorkflow) => {
    try {
      const newStatus = workflow.status === 'Active' ? 'Draft' : 'Active'
      await approvalWorkflowService.updateWorkflow(workflow.id, { status: newStatus })
      await loadWorkflows()
    } catch (err) {
      console.error('Failed to toggle status:', err)
    }
  }

  const handleSaveWorkflow = async (name: string, description: string | undefined) => {
    if (!editingWorkflow) return

    setSaving(true)
    try {
      await approvalWorkflowService.updateWorkflow(editingWorkflow.id, { name, description })
      await loadWorkflows()
      // Reload the editing workflow to get fresh data
      const updated = await approvalWorkflowService.getWorkflow(editingWorkflow.id)
      setEditingWorkflow(updated)
    } finally {
      setSaving(false)
    }
  }

  const handleAddStage = async (stage: CreateApprovalStageRequest) => {
    if (!editingWorkflow) return

    await approvalWorkflowService.addStage(editingWorkflow.id, stage)
    const updated = await approvalWorkflowService.getWorkflow(editingWorkflow.id)
    setEditingWorkflow(updated)
  }

  const handleUpdateStage = async (stageId: string, updates: Partial<ApprovalStage>) => {
    if (!editingWorkflow) return

    await approvalWorkflowService.updateStage(editingWorkflow.id, stageId, updates)
    const updated = await approvalWorkflowService.getWorkflow(editingWorkflow.id)
    setEditingWorkflow(updated)
  }

  const handleRemoveStage = async (stageId: string) => {
    if (!editingWorkflow) return

    await approvalWorkflowService.removeStage(editingWorkflow.id, stageId)
    const updated = await approvalWorkflowService.getWorkflow(editingWorkflow.id)
    setEditingWorkflow(updated)
  }

  const handleAddApprover = async (stageId: string, approver: AddStageApproverRequest) => {
    if (!editingWorkflow) return

    await approvalWorkflowService.addApprover(editingWorkflow.id, stageId, approver)
    const updated = await approvalWorkflowService.getWorkflow(editingWorkflow.id)
    setEditingWorkflow(updated)
  }

  const handleRemoveApprover = async (stageId: string, approverId: string) => {
    if (!editingWorkflow) return

    await approvalWorkflowService.removeApprover(editingWorkflow.id, stageId, approverId)
    const updated = await approvalWorkflowService.getWorkflow(editingWorkflow.id)
    setEditingWorkflow(updated)
  }

  // If editing a workflow, show the builder
  if (editingWorkflow) {
    return (
      <>
        <PageMeta
          title="Edit Approval Workflow | Admin"
          description="Configure approval workflow stages and approvers"
        />
        <WorkflowBuilder
          workflow={editingWorkflow}
          onBack={() => {
            setEditingWorkflow(null)
            loadWorkflows()
          }}
          onSave={handleSaveWorkflow}
          onAddStage={handleAddStage}
          onUpdateStage={handleUpdateStage}
          onRemoveStage={handleRemoveStage}
          onAddApprover={handleAddApprover}
          onRemoveApprover={handleRemoveApprover}
          saving={saving}
        />
      </>
    )
  }

  const activeCount = workflows.filter((w) => w.status === 'Active').length
  const totalStages = workflows.reduce((acc, w) => acc + w.stageCount, 0)

  return (
    <PermissionGate
      permissions={['workflow.admin']}
      fallback={
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <ShieldAlert className="h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-500 dark:text-gray-400">You don't have permission to manage approval workflows.</p>
        </div>
      }
    >
    <>
      <PageMeta
        title="Approval Workflows | Admin"
        description="Manage template approval workflows"
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              Approval Workflows
            </h1>
            <p className="text-neutral-500 dark:text-neutral-400 mt-1">
              Configure multi-stage review processes for template publishing
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', refreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Workflow
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
            <GitBranch className="h-4 w-4 text-neutral-500" />
            <span className="text-sm text-neutral-600 dark:text-neutral-300">
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                {workflows.length}
              </span>{' '}
              workflow{workflows.length !== 1 ? 's' : ''}
            </span>
          </div>
          <Badge variant="success" className="py-2">
            {activeCount} active
          </Badge>
          <Badge variant="secondary" className="py-2">
            {totalStages} total stages
          </Badge>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/50 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={loadWorkflows}>
              Retry
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading workflows...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && workflows.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-4 border border-dashed border-neutral-200 dark:border-neutral-700 rounded-lg">
            <GitBranch className="h-12 w-12 text-neutral-400" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              No approval workflows configured
            </h3>
            <p className="text-neutral-500 dark:text-neutral-400 text-center max-w-md">
              Create your first approval workflow to define how templates are reviewed and approved
              before publishing.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Workflow
            </Button>
          </div>
        )}

        {/* Workflows List */}
        {!loading && workflows.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            {workflows.map((workflow) => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onEdit={() => handleEdit(workflow)}
                onDelete={() => setDeleteWorkflow(workflow)}
                onSetDefault={() => handleSetDefault(workflow)}
                onToggleStatus={() => handleToggleStatus(workflow)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <CreateWorkflowDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreate={handleCreate}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteWorkflow} onOpenChange={(open) => !open && setDeleteWorkflow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Approval Workflow</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteWorkflow?.name}"? This will remove all
              associated stages and approvers. Active approval requests using this workflow will not
              be affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteWorkflow(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
    </PermissionGate>
  )
}
